/**
 * auvo — Bowl Configurator
 *
 * Handles:
 *  - Top color / bottom color swatch selection
 *  - Pet name input → live preview overlay
 *  - Size variant selection (true variant) → updates hidden #id + price
 *  - URL query param hydration (?top=...&bottom=...&name=...)
 *  - Sets line item properties for cart submission
 *
 * Preview is Phase 1 (CSS-only stylized disc). When real bowl-base.png + masks
 * are dropped into /assets, swap the preview markup in bowl-configurator.liquid
 * and only the color-update calls below need to change (they already set
 * CSS custom properties, which works identically with masks).
 */

class BowlConfigurator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-cfg-form]');
    this.mainImage = this.querySelector('[data-main-image]');
    this.baseSrc = this.mainImage ? this.mainImage.getAttribute('src') : '';
    this.nameInput = this.querySelector('[data-name-input]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.qtyInput = this.querySelector('[data-qty-input]');
    this.qtyDisplay = this.querySelector('[data-qty-display]');
    this.priceEl = this.querySelector('[data-cfg-price]');
    this.propTop = this.querySelector('[data-prop-top]');
    this.propBottom = this.querySelector('[data-prop-bottom]');
    this.propName = this.querySelector('[data-prop-name]');
    this.topNameEl = this.querySelector('[data-top-name]');
    this.bottomNameEl = this.querySelector('[data-bottom-name]');
    this.previewBowl = this.querySelector('[data-preview-bowl]');
    this.previewName = this.querySelector('[data-preview-name]');
    this.engraveName = this.querySelector('[data-engrave-name]');
    this.engraveTopDot = this.querySelector('[data-engrave-top-dot]');
    this.engraveBottomDot = this.querySelector('[data-engrave-bottom-dot]');
    this.engraveTopName = this.querySelector('[data-engrave-top-name]');
    this.engraveBottomName = this.querySelector('[data-engrave-bottom-name]');

    this.topSwatches = Array.from(this.querySelectorAll('[data-top-swatch]'));
    this.bottomSwatches = Array.from(this.querySelectorAll('[data-bottom-swatch]'));
    this.sizeButtons = Array.from(this.querySelectorAll('[data-size]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

    this.state = {
      topColor: this.dataset.defaultTop,
      topName: '',
      bottomColor: this.dataset.defaultBottom,
      bottomName: '',
      petName: '',
      variantId: this.variantInput?.value || '',
      qty: 1
    };

    this.hydrateFromURL();
    this.bindEvents();
    this.applyInitialState();
    this.render();
  }

  hydrateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const topHandle = params.get('top');
    const bottomHandle = params.get('bottom');
    const name = params.get('name');
    if (topHandle) {
      const sw = this.topSwatches.find((s) => s.dataset.handle === topHandle);
      if (sw) { this.state.topColor = sw.dataset.color; this.state.topName = sw.dataset.name; }
    }
    if (bottomHandle) {
      const sw = this.bottomSwatches.find((s) => s.dataset.handle === bottomHandle);
      if (sw) { this.state.bottomColor = sw.dataset.color; this.state.bottomName = sw.dataset.name; }
    }
    // Sanitize URL-provided names: the maxlength attribute only constrains
    // typing, so a shared/ad link could otherwise inject an over-long or
    // un-debossable string straight into the order.
    if (name) this.state.petName = this.sanitizeName(name);
  }

  sanitizeName(raw) {
    const maxLen = parseInt(this.nameInput?.getAttribute('maxlength'), 10) || 12;
    return String(raw)
      .toUpperCase()
      .replace(/[^A-Z0-9 '\-]/g, '')
      .slice(0, maxLen);
  }

  applyInitialState() {
    // Only pre-fill the input when there's a real name (e.g. from a ?name= URL).
    // Never seed it from the default — that would submit a stray engraving on
    // orders where the customer left the optional field blank.
    if (this.nameInput && this.state.petName) this.nameInput.value = this.state.petName;

    const topMatch =
      this.topSwatches.find((s) => s.dataset.color?.toLowerCase() === this.state.topColor?.toLowerCase()) ||
      this.topSwatches[0];
    if (topMatch) {
      this.state.topColor = topMatch.dataset.color;
      this.state.topName = topMatch.dataset.name;
      this.setPressed(this.topSwatches, topMatch);
    }

    const bottomMatch =
      this.bottomSwatches.find((s) => s.dataset.color?.toLowerCase() === this.state.bottomColor?.toLowerCase()) ||
      this.bottomSwatches[0];
    if (bottomMatch) {
      this.state.bottomColor = bottomMatch.dataset.color;
      this.state.bottomName = bottomMatch.dataset.name;
      this.setPressed(this.bottomSwatches, bottomMatch);
    }
  }

  bindEvents() {
    this.topSwatches.forEach((btn) => {
      btn.addEventListener('click', () => this.selectSwatch('top', btn));
    });
    this.bottomSwatches.forEach((btn) => {
      btn.addEventListener('click', () => this.selectSwatch('bottom', btn));
    });

    // Arrows: step selection within the strip they belong to
    this.querySelectorAll('[data-strip-prev], [data-strip-next]').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const strip = arrow.closest('.auvo-cfg-' + this.dataset.sectionId + '__strip') || arrow.parentElement;
        const isTop = !!strip.querySelector('[data-top-swatch]');
        const group = isTop ? this.topSwatches : this.bottomSwatches;
        const dir = arrow.hasAttribute('data-strip-next') ? 1 : -1;
        const currentIdx = group.findIndex((b) => b.getAttribute('aria-pressed') === 'true');
        const next = (currentIdx + dir + group.length) % group.length;
        this.selectSwatch(isTop ? 'top' : 'bottom', group[next]);
        group[next].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    });

    this.sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.state.variantId = btn.dataset.variantId;
        this.setPressed(this.sizeButtons, btn);
        if (this.variantInput) this.variantInput.value = btn.dataset.variantId;
        if (this.priceEl && btn.dataset.price) this.priceEl.textContent = btn.dataset.price;
      });
    });

    this.thumbs.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.hasAttribute('data-live')) {
          // Back to the live configurable bowl: restore base image, show tints/name
          if (this.mainImage) this.mainImage.src = this.baseSrc;
          this.previewBowl?.classList.remove('is-photo');
        } else if (this.mainImage && btn.dataset.full) {
          // A photo: show it cleanly, hide the colour tints + engraved name
          this.mainImage.src = btn.dataset.full;
          this.previewBowl?.classList.add('is-photo');
        }
        this.setPressed(this.thumbs, btn);
      });
    });

    if (this.nameInput) {
      this.nameInput.addEventListener('input', () => {
        const caret = this.nameInput.selectionStart;
        const before = this.nameInput.value;
        const clean = this.sanitizeName(before);
        const removed = before.length - clean.length;
        this.nameInput.value = clean;
        const pos = Math.max(0, (caret ?? clean.length) - removed);
        this.nameInput.setSelectionRange?.(pos, pos);
        this.state.petName = clean;
        this.render();
      });
    }

    this.querySelector('[data-qty-up]')?.addEventListener('click', () => this.changeQty(1));
    this.querySelector('[data-qty-down]')?.addEventListener('click', () => this.changeQty(-1));
    this.qtyDisplay?.addEventListener('change', () => {
      const v = parseInt(this.qtyDisplay.value, 10);
      this.state.qty = Number.isFinite(v) && v > 0 ? v : 1;
      this.syncQty();
    });

    this.querySelector('[data-buy-now]')?.addEventListener('click', () => this.buyNow());

    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        if (!this.populateAndValidate()) { e.preventDefault(); return; }
        // Theme cart type is "drawer": add via cart/add.js and open the drawer
        // instead of navigating away to /cart. Falls through to the native
        // full-page POST when the drawer isn't available (no-JS parity).
        const drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.renderContents !== 'function' || !window.fetch) return;
        e.preventDefault();
        this.addToCartViaDrawer(drawer);
      });
    }
  }

  selectSwatch(which, btn) {
    if (!btn) return;
    if (this.previewBowl?.classList.contains('is-photo')) {
      // Picking a colour while a product photo is shown: jump back to the
      // live bowl so the new tint is visible on the main image, not just the thumb
      if (this.mainImage) this.mainImage.src = this.baseSrc;
      this.previewBowl.classList.remove('is-photo');
      const liveThumb = this.thumbs.find((t) => t.hasAttribute('data-live'));
      if (liveThumb) this.setPressed(this.thumbs, liveThumb);
    }
    if (which === 'top') {
      this.state.topColor = btn.dataset.color;
      this.state.topName = btn.dataset.name;
      this.setPressed(this.topSwatches, btn);
    } else {
      this.state.bottomColor = btn.dataset.color;
      this.state.bottomName = btn.dataset.name;
      this.setPressed(this.bottomSwatches, btn);
    }
    this.render();
  }

  changeQty(delta) {
    this.state.qty = Math.max(1, this.state.qty + delta);
    this.syncQty();
  }

  syncQty() {
    if (this.qtyDisplay) this.qtyDisplay.value = this.state.qty;
    if (this.qtyInput) this.qtyInput.value = this.state.qty;
  }

  setPressed(group, target) {
    group.forEach((b) => b.setAttribute('aria-pressed', b === target ? 'true' : 'false'));
  }

  populateAndValidate() {
    if (this.propTop) this.propTop.value = this.state.topName || '';
    if (this.propBottom) this.propBottom.value = this.state.bottomName || '';
    // Submit the same normalized value the preview shows.
    if (this.propName) this.propName.value = this.sanitizeName(this.state.petName || '').trim();
    if (this.qtyInput) this.qtyInput.value = this.state.qty;
    return true;
  }

  async addToCartViaDrawer(drawer) {
    const root = window.Shopify?.routes?.root || '/';
    const data = new FormData(this.form);
    if (typeof drawer.getSectionsToRender === 'function') {
      data.append('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
      data.append('sections_url', window.location.pathname);
    }
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data
      });
      const state = await res.json();
      if (!res.ok || state.status) throw new Error(state.description || 'add failed');
      drawer.renderContents(state);
    } catch (err) {
      this.form.submit();
    }
  }

  async buyNow() {
    if (!this.populateAndValidate() || !this.form) return;
    const root = window.Shopify?.routes?.root || '/';
    const data = new FormData(this.form);
    try {
      const res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data
      });
      if (!res.ok) throw new Error('add failed');
      window.location.href = root + 'checkout';
    } catch (e) {
      this.form.submit();
    }
  }

  render() {
    if (this.topNameEl) this.topNameEl.textContent = this.state.topName || '';
    if (this.bottomNameEl) this.bottomNameEl.textContent = this.state.bottomName || '';

    // Live bowl preview — tint the gray base bowl with the chosen colors.
    // Set on the root element so BOTH the main preview and the mini "your
    // design" thumbnail (which inherit these vars) update together.
    if (this.state.topColor) this.style.setProperty('--top-color', this.state.topColor);
    if (this.state.bottomColor) this.style.setProperty('--bottom-color', this.state.bottomColor);

    // True engraving = exactly what the customer typed (blank → nothing engraved).
    const typed = (this.state.petName || '').trim().toUpperCase();
    // The decorative on-bowl sample (hidden) may show the example name; the
    // customer-facing "your design" caption must reflect what actually ships.
    const sample = typed || (this.dataset.defaultName || '').toUpperCase();
    if (this.previewName) this.previewName.textContent = sample;
    if (this.engraveName) this.engraveName.textContent = typed;
    if (this.engraveTopDot && this.state.topColor) this.engraveTopDot.style.background = this.state.topColor;
    if (this.engraveBottomDot && this.state.bottomColor) this.engraveBottomDot.style.background = this.state.bottomColor;
    if (this.engraveTopName) this.engraveTopName.textContent = this.state.topName || '';
    if (this.engraveBottomName) this.engraveBottomName.textContent = this.state.bottomName || '';
  }
}

customElements.define('bowl-configurator', BowlConfigurator);
